import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { queryClient } from 'src/lib/queryClient';
import { getMe } from './api';
import type { Me } from './types';

// type AuthState = { me: Me | null; loading: boolean; reload: () => Promise<void> };
type AuthState = {
    me: Me | null;
    loading: boolean;
    isLoggingOut: boolean;
    reload: () => Promise<void>;
    logout: () => void;
    applyAuthUser: (user: Me | null) => void;
};
// const Ctx = createContext<AuthState>({ me: null, loading: true, reload: async () => {} });
const Ctx = createContext<AuthState>({
    me: null,
    loading: true,
    isLoggingOut: false,
    reload: async () => {},
    logout: () => {},
    applyAuthUser: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [me, setMe] = useState<Me | null>(null);
    const [loading, setLoading] = useState(true);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const applyAuthUser = (user: Me | null) => {
        if (typeof window !== 'undefined') {
            if (user) {
                localStorage.setItem('me', JSON.stringify(user));
            } else {
                localStorage.removeItem('me');
            }
        }
        setMe(user);
    };

    const logout = () => {
        setIsLoggingOut(true);
        if (typeof window !== 'undefined') {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('me');
        }

        queryClient.clear();
        setMe(null);
        // 필요하면 여기서 location.replace('/login')같은 리디렉션 처리 가능
    };

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
            if (!token) {
                applyAuthUser(null);
                return;
            }
            setIsLoggingOut(false);
            // 실제 사용자 정보 호출
            const user = await getMe();
            applyAuthUser(user ?? null);
        } catch (err: any) {
            // 401등 에러 시 토큰 제거 후 비로그인 처리
            // setMe(null);
            const status = err?.response?.status;
            const msg = err?.response?.data?.error?.message ?? err?.response?.data?.message;
            if (status === 403 && msg === 'LockedUser') {
                // ✅ 잠김은 "비로그인"이 아니라 "잠김 상태"
                setMe({
                    id: 'locked',
                    email: '',
                    name: '',
                    status: 'LOCKED',
                    mustChangePassword: false,
                    isGuestOnlyUser: false,
                    isServiceAdmin: false,
                    isCompanyAdmin: false,
                    companyId: null,
                });
                return;
            }
            // 그 외(401 등) → 비로그인 처리
            if (status === 401 && typeof window !== 'undefined') {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('me');
            }
            applyAuthUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    return <Ctx.Provider value={{ me, loading, isLoggingOut, reload, logout, applyAuthUser }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
